/**
 * Firestore service for Super Admin-managed GIF skins.
 *
 * Skins are stored in the `gifSkins` Firestore collection.
 * Only Super Admins can create / edit / delete skins.
 * All clients subscribe to the enabled skins for the picker and rotation.
 *
 * Collection: `gifSkins`
 */
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

// ── Schema ────────────────────────────────────────────────────────────────────

export interface AdminGifSkin {
  /** Firestore document ID */
  id: string;
  /** Display name shown in the picker */
  name: string;
  /** Free-text category for grouping (e.g. "Nature", "Neon", "Genre") */
  category: string;
  /** Publicly accessible GIF/looping-video URL */
  bodyUrl: string;
  /** Overlay opacity for the GIF backdrop (0.3–1.0). Default 0.72 */
  bodyOpacity: number;
  /** Base hue for colour tokens when this skin is active (0–360) */
  baseHue: number;
  /** Whether the skin is visible in the picker and eligible for rotation */
  enabled: boolean;
  /** Sort order (lower = earlier in the list) */
  order: number;
  createdBy?: string;
  createdAt?: Timestamp;
  /** Whether the skin has been shared to the community gallery */
  shared?: boolean;
  /** UID of the user who shared the skin to the community */
  sharedByUid?: string;
}

const COL = 'gifSkins';

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * Real-time subscription to all GIF skins, ordered by `order` ascending.
 * Falls back to a one-shot fetch if the index hasn't been built yet.
 */
export function subscribeGifSkins(
  callback: (skins: AdminGifSkin[]) => void,
): () => void {
  const q = query(collection(db, COL), orderBy('order', 'asc'));
  const unsub = onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as AdminGifSkin)));
    },
    // Index may not exist yet — fall back to unordered one-shot
    async () => {
      try {
        const snap = await getDocs(collection(db, COL));
        const skins = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as AdminGifSkin))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        callback(skins);
      } catch {
        callback([]);
      }
    },
  );
  return unsub;
}

export async function getAllGifSkins(): Promise<AdminGifSkin[]> {
  try {
    const snap = await getDocs(query(collection(db, COL), orderBy('order', 'asc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as AdminGifSkin));
  } catch {
    try {
      const snap = await getDocs(collection(db, COL));
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() } as AdminGifSkin))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    } catch {
      return [];
    }
  }
}

// ── Write ─────────────────────────────────────────────────────────────────────

export async function addGifSkin(
  skin: Omit<AdminGifSkin, 'id' | 'createdAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COL), {
    ...skin,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateGifSkin(
  id: string,
  updates: Partial<Omit<AdminGifSkin, 'id' | 'createdAt'>> & Record<string, unknown>,
): Promise<void> {
  await updateDoc(doc(db, COL, id), updates);
}

export async function deleteGifSkin(id: string): Promise<void> {
  await deleteDoc(doc(db, COL, id));
}
